/**
 * PRP-WEB-1 W2.7: the Intake tab's "Answered in chat" list (GET /projects/{id}/chat-answers):
 * per estimate, each question the chat asked with the answer the user gave, then the questions
 * still waiting in the chat. Read only; nothing renders when there is nothing to show or the
 * route is not there (older server). Question and answer text is data, shown as text.
 */
import { useEffect, useState } from 'react';

import { ProjectOverviewAPI } from '../../lib/projectOverviewApi';
import type { ChatAnswersGroup } from '../../lib/projectOverviewApi';
import { changedAgo, exactTime } from './EstimateCard';
import { Pill } from './ui/pill';

export default function ChatAnswersSection({ projectId }: { projectId: string }) {
  const [groups, setGroups] = useState<ChatAnswersGroup[]>([]);

  useEffect(() => {
    let cancelled = false;
    ProjectOverviewAPI.getChatAnswers(projectId)
      .then((g) => {
        if (!cancelled) setGroups(g);
      })
      .catch(() => {
        if (!cancelled) setGroups([]);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  if (groups.length === 0) return null;
  const many = groups.length > 1;

  return (
    <section
      aria-labelledby="chat-answers-h"
      className="mt-4 rounded-[var(--r-md)] border border-[var(--bd-subtle)] p-3"
      data-testid="chat-answers"
    >
      <h3 id="chat-answers-h" className="text-[14px] font-[600] text-[var(--tx-hi)]">
        Answered in chat
      </h3>
      {groups.map((g) => (
        <div key={g.parse_hash} className="mt-2" data-testid={`chat-answers-${g.parse_hash}`}>
          {many && <div className="mb-1 text-[12px] font-[600] text-[var(--tx-mid)]">{g.name}</div>}
          <ul className="flex flex-col divide-y divide-[var(--bd-subtle)]">
            {g.answered.map((a, i) => (
              <li key={`a${i}`} className="py-2 text-[12.5px]" data-testid="chat-answer">
                <div className="break-words text-[var(--tx-mid)]">{a.question}</div>
                <div className="mt-0.5 break-words font-[560] text-[var(--tx-hi)]">
                  {a.answer || '(no answer text)'}
                  {a.answered_at && (
                    <span className="ml-2 font-[450] text-[11.5px] text-[var(--tx-faint)]" title={exactTime(a.answered_at)}>
                      {changedAgo(a.answered_at).replace(/^changed /, '')}
                    </span>
                  )}
                </div>
              </li>
            ))}
            {g.waiting.map((w, i) => (
              <li key={`w${i}`} className="py-2 text-[12.5px]" data-testid="chat-waiting">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="min-w-0 break-words text-[var(--tx-mid)]">{w.question}</span>
                  <Pill kind="warn">Waiting in chat</Pill>
                </div>
                {w.options.length > 0 && (
                  <div className="mt-0.5 break-words text-[11.5px] text-[var(--tx-lo)]">Options: {w.options.join(' · ')}</div>
                )}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </section>
  );
}
